import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { formatPKR } from '../utils/currencyFormatter';
import { API } from '../config';
import axios from 'axios';
import './Dashboard.css';

const getDateRange = (period) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let from, to;
  if (period === 'today') {
    from = to = today;
  } else if (period === 'week') {
    const day = today.getDay();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
    from = startOfWeek;
    to = new Date(today);
    to.setHours(23, 59, 59, 999);
  } else {
    from = new Date(today.getFullYear(), today.getMonth(), 1);
    to = new Date(today);
    to.setHours(23, 59, 59, 999);
  }
  return {
    from: from.toISOString().split('T')[0],
    to: to.toISOString().split('T')[0]
  };
};

const Dashboard = () => {
  const { user } = useAuth();
  const { effectiveBranchId, currentBranch } = useBranch();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentReceived, setPaymentReceived] = useState(null);
  const [paymentReceivedPeriod, setPaymentReceivedPeriod] = useState('month');
  const [shopExpense, setShopExpense] = useState(null);
  const [shopExpensePeriod, setShopExpensePeriod] = useState('month');
  const [shopExpenseFrom, setShopExpenseFrom] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [shopExpenseTo, setShopExpenseTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [salaryByMonth, setSalaryByMonth] = useState([]);

  const branchParam = effectiveBranchId ? `&branchId=${effectiveBranchId}` : '';

  useEffect(() => {
    fetchDashboardData();
    fetchRecentActivities();
  }, [effectiveBranchId]);

  useEffect(() => {
    axios.get(`${API}/dashboard/salary-by-month?limit=12${branchParam}`)
      .then(res => setSalaryByMonth(res.data.salaryByMonth || []))
      .catch(() => setSalaryByMonth([]));
  }, [effectiveBranchId]);

  useEffect(() => {
    const { from, to } = getDateRange(paymentReceivedPeriod);
    axios.get(`${API}/dashboard/payment-received?from=${from}&to=${to}${branchParam}`)
      .then(res => setPaymentReceived(res.data))
      .catch(() => setPaymentReceived({ cash: 0, cheque: 0, bank_transfer: 0, card: 0, other: 0, total: 0 }));
  }, [paymentReceivedPeriod, effectiveBranchId]);

  useEffect(() => {
    const from = shopExpensePeriod === 'custom' ? shopExpenseFrom : getDateRange(shopExpensePeriod).from;
    const to = shopExpensePeriod === 'custom' ? shopExpenseTo : getDateRange(shopExpensePeriod).to;
    axios.get(`${API}/dashboard/shop-expense?from=${from}&to=${to}${branchParam}`)
      .then(res => setShopExpense(res.data))
      .catch(() => setShopExpense({ total: 0, entries: [] }));
  }, [shopExpensePeriod, effectiveBranchId, shopExpenseFrom, shopExpenseTo]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const url = effectiveBranchId ? `${API}/dashboard/stats?branchId=${effectiveBranchId}` : `${API}/dashboard/stats`;
      const response = await axios.get(url);
      setStats(response.data.stats);
    } catch (err) {
      console.error('Dashboard error:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivities = async () => {
    try {
      const businessId = user?.business_id || user?.businessId || '';
      const url = `${API}/activities/business/${businessId}/recent?limit=10${branchParam}`;
      const response = await axios.get(url);
      setActivities(response.data.activities || []);
    } catch (err) {
      console.error('Activities error:', err);
    }
  };

  const getActionIcon = (action) => {
    switch(action) {
      case 'create': return '➕';
      case 'update': return '✏️';
      case 'delete': return '🗑️';
      case 'confirm': return '✅';
      case 'cancel': return '❌';
      case 'payment': return '💵';
      default: return '📝';
    }
  };

  const getActionColor = (action) => {
    switch(action) {
      case 'create': return 'action-create';
      case 'update': return 'action-update';
      case 'delete': return 'action-delete';
      case 'confirm': return 'action-confirm';
      case 'cancel': return 'action-cancel';
      default: return '';
    }
  };

  const formatActivityTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };


  if (loading) {
    return (
      <Layout>
        <div className="dashboard">
          <h1>Dashboard</h1>
          <Loader message="Loading dashboard data..." variant="page" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="dashboard">
        <h1>Dashboard</h1>
        {effectiveBranchId && currentBranch && (
          <p className="dashboard-branch-hint">Viewing branch: <strong>{currentBranch.name}</strong></p>
        )}
        {error && <div className="error-banner">{error}</div>}

        {stats && (
          <>
            {/* Main Stats */}
            <div className="stats-grid">
              <div className="stat-card" onClick={() => navigate('/products')}>
                <div className="stat-icon products-icon">📦</div>
                <div className="stat-content">
                  <div className="stat-value">{stats.products?.total || 0}</div>
                  <div className="stat-label">Total Products</div>
                  <div className="stat-sub">{stats.products?.variants || 0} variants</div>
                </div>
              </div>

              <div className="stat-card" onClick={() => navigate('/sales')}>
                <div className="stat-icon sales-icon">💰</div>
                <div className="stat-content">
                  <div className="stat-value">{stats.sales?.count || 0}</div>
                  <div className="stat-label">Total Sales</div>
                  <div className="stat-sub">{formatPKR(stats.sales?.revenue)}</div>
                </div>
              </div>

              <div className="stat-card highlight">
                <div className="stat-icon revenue-icon">📈</div>
                <div className="stat-content">
                  <div className="stat-value">{formatPKR(stats.sales?.thisMonth?.total)}</div>
                  <div className="stat-label">This Month</div>
                  <div className="stat-sub">{stats.sales?.thisMonth?.count || 0} sales</div>
                </div>
              </div>

              <div className="stat-card" onClick={() => navigate('/parties')}>
                <div className="stat-icon customers-icon">👥</div>
                <div className="stat-content">
                  <div className="stat-value">{stats.parties?.customers || 0}</div>
                  <div className="stat-label">Customers</div>
                  <div className="stat-sub">{stats.parties?.total || 0} total parties</div>
                </div>
              </div>
            </div>

            {/* Total in Banks/Cash */}
            <div className="balance-summary-section">
              <h2 className="balance-summary-title">Cash &amp; Bank</h2>
              <div className="balance-summary-cards">
                <div className="balance-card cash-bank-card" onClick={() => navigate('/accounts')}>
                  <div className="balance-card-icon">🏦</div>
                  <div className="balance-card-content">
                    <div className="balance-card-label">Total balance in banks &amp; cash</div>
                    <div className="balance-card-value">{formatPKR(stats.cashBankBalance ?? stats.cash_bank_balance ?? 0)}</div>
                    <div className="balance-card-hint">Combined balance across all cash and bank accounts</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Money owed: parties owe us / we owe (branch-based) */}
            <div className="balance-summary-section">
              <h2 className="balance-summary-title">
                Outstanding balances
                {(stats.partyBalances?.branchName || stats.party_balances?.branch_name) && (
                  <span className="balance-summary-branch"> ({stats.partyBalances?.branchName ?? stats.party_balances?.branch_name})</span>
                )}
              </h2>
              <div className="balance-summary-cards">
                <div className="balance-card receivable-card">
                  <div className="balance-card-icon">📥</div>
                  <div className="balance-card-content">
                    <div className="balance-card-label">Parties owe you</div>
                    <div className="balance-card-value">{formatPKR(stats.partyBalances?.totalReceivable ?? stats.party_balances?.total_receivable ?? 0)}</div>
                    <div className="balance-card-hint">Total receivable from customers & parties</div>
                  </div>
                </div>
                <div className="balance-card payable-card">
                  <div className="balance-card-icon">📤</div>
                  <div className="balance-card-content">
                    <div className="balance-card-label">You owe</div>
                    <div className="balance-card-value">{formatPKR(stats.partyBalances?.totalPayable ?? stats.party_balances?.total_payable ?? 0)}</div>
                    <div className="balance-card-hint">Total payable to vendors & tailors</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary Stats */}
            <div className="secondary-stats">
              <div className="stat-mini">
                <span className="stat-mini-label">Today's Sales</span>
                <span className="stat-mini-value">{formatPKR(stats.sales?.today?.total)} ({stats.sales?.today?.count || 0})</span>
              </div>
              <div className="stat-mini">
                <span className="stat-mini-label">Receivable (sales)</span>
                <span className="stat-mini-value receivable">{formatPKR(stats.sales?.receivable)}</span>
              </div>
              <div className="stat-mini">
                <span className="stat-mini-label">Payable (purchases)</span>
                <span className="stat-mini-value payable">{formatPKR(stats.purchases?.payable)}</span>
              </div>
              <div className="stat-mini">
                <span className="stat-mini-label">Vendors</span>
                <span className="stat-mini-value">{stats.parties?.vendors || 0}</span>
              </div>
              <div className="stat-mini">
                <span className="stat-mini-label">Tailors</span>
                <span className="stat-mini-value">{stats.parties?.tailors || 0}</span>
              </div>
              <div className="stat-mini stat-mini-clickable" onClick={() => navigate('/products')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate('/products')}>
                <span className="stat-mini-label">Total Stock (value)</span>
                <span className="stat-mini-value">{formatPKR(stats.inventory?.totalStockValue ?? stats.inventory?.total_stock_value ?? 0)}</span>
                <span className="stat-mini-sub">{(stats.inventory?.totalStock ?? stats.inventory?.total_stock ?? 0)} units</span>
              </div>
            </div>

            {/* Salary paid by month */}
            <div className="dashboard-section salary-by-month-section">
              <h2>Salary paid by month (PKR)</h2>
              {salaryByMonth.length > 0 ? (
                <div className="salary-by-month-table-wrap">
                  <table className="salary-by-month-table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Amount paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salaryByMonth.map((row) => (
                        <tr key={`${row.year}-${row.month}`}>
                          <td>{row.monthLabel}</td>
                          <td className="salary-amount">{formatPKR(row.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">No salary payments recorded yet.</div>
              )}
            </div>

            {/* Payment Received */}
            <div className="dashboard-section payment-received-section">
              <h2>Payment Received (PKR)</h2>
              <div className="payment-received-controls">
                <label>Period:</label>
                <select value={paymentReceivedPeriod} onChange={e => setPaymentReceivedPeriod(e.target.value)} className="payment-received-select">
                  <option value="today">Today</option>
                  <option value="week">This week</option>
                  <option value="month">This month</option>
                </select>
              </div>
              {paymentReceived && (
                <div className="payment-received-grid">
                  <div className="payment-received-row">
                    <span className="payment-received-label">Cash</span>
                    <span className="payment-received-value">{formatPKR(paymentReceived.cash)}</span>
                  </div>
                  <div className="payment-received-row">
                    <span className="payment-received-label">Cheque</span>
                    <span className="payment-received-value">{formatPKR(paymentReceived.cheque)}</span>
                  </div>
                  <div className="payment-received-row">
                    <span className="payment-received-label">Bank transfer</span>
                    <span className="payment-received-value">{formatPKR(paymentReceived.bank_transfer)}</span>
                  </div>
                  {(paymentReceived.card > 0 || paymentReceived.other > 0) && (
                    <>
                      {paymentReceived.card > 0 && (
                        <div className="payment-received-row">
                          <span className="payment-received-label">Card</span>
                          <span className="payment-received-value">{formatPKR(paymentReceived.card)}</span>
                        </div>
                      )}
                      {paymentReceived.other > 0 && (
                        <div className="payment-received-row">
                          <span className="payment-received-label">Other</span>
                          <span className="payment-received-value">{formatPKR(paymentReceived.other)}</span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="payment-received-row total">
                    <span className="payment-received-label">Total</span>
                    <span className="payment-received-value">{formatPKR(paymentReceived.total)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Shop Expense */}
            <div className="dashboard-section shop-expense-section">
              <h2>Shop Expense (PKR)</h2>
              <div className="payment-received-controls">
                <label>Period:</label>
                <select value={shopExpensePeriod} onChange={e => setShopExpensePeriod(e.target.value)} className="payment-received-select">
                  <option value="today">Today</option>
                  <option value="week">This week</option>
                  <option value="month">This month</option>
                  <option value="custom">Custom dates</option>
                </select>
                {shopExpensePeriod === 'custom' && (
                  <>
                    <label className="shop-expense-date-label">From</label>
                    <input
                      type="date"
                      value={shopExpenseFrom}
                      onChange={e => setShopExpenseFrom(e.target.value)}
                      className="payment-received-select shop-expense-date"
                    />
                    <label className="shop-expense-date-label">To</label>
                    <input
                      type="date"
                      value={shopExpenseTo}
                      onChange={e => setShopExpenseTo(e.target.value)}
                      className="payment-received-select shop-expense-date"
                    />
                  </>
                )}
              </div>
              {shopExpense && (
                <>
                  <div className="shop-expense-total">
                    <span className="shop-expense-label">
                      {shopExpensePeriod === 'custom' ? `Total (${shopExpense.fromDate} – ${shopExpense.toDate})` : 'Total shop expense'}
                    </span>
                    <span className="shop-expense-value">{formatPKR(shopExpense.total)}</span>
                  </div>
                  {Array.isArray(shopExpense.entries) && shopExpense.entries.length > 0 && (
                    <div className="shop-expense-list-wrap">
                      <table className="shop-expense-list">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Description</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {shopExpense.entries.map((entry) => (
                            <tr key={entry.id}>
                              <td>{entry.transaction_date ? new Date(entry.transaction_date).toLocaleDateString() : '—'}</td>
                              <td className="shop-expense-desc">{entry.description || '—'}</td>
                              <td className="shop-expense-amount">{formatPKR(entry.debit)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="dashboard-content">
              <div className="dashboard-section">
                <h2>Recent Sales</h2>
                {stats.recentSales && stats.recentSales.length > 0 ? (
                  <div className="recent-list">
                    {stats.recentSales.map((sale, index) => (
                      <div key={index} className="recent-item">
                        <div className="recent-info">
                          <span className="recent-invoice">{sale.invoiceNumber}</span>
                          <span className="recent-customer">{sale.customer}</span>
                        </div>
                        <div className="recent-meta">
                          <span className="recent-branch">{sale.branch}</span>
                          <span className="recent-amount">{formatPKR(sale.total)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">No recent sales to display.</div>
                )}
              </div>

              <div className="dashboard-section">
                <h2>Quick Actions</h2>
                <div className="quick-actions">
                  <button className="action-button primary" onClick={() => navigate('/sales')}>
                    Create New Sale
                  </button>
                  <button className="action-button" onClick={() => navigate('/products')}>
                    Add Product
                  </button>
                  <button className="action-button" onClick={() => navigate('/parties')}>
                    Add Party
                  </button>
                </div>
              </div>
            </div>

            {/* Recent Activities Section */}
            <div className="dashboard-section activities-section">
              <h2>Recent Activities</h2>
              {activities.length > 0 ? (
                <div className="activities-list">
                  {activities.map((activity) => (
                    <div key={activity.id} className="activity-item">
                      <div className={`activity-icon ${getActionColor(activity.action)}`}>
                        {getActionIcon(activity.action)}
                      </div>
                      <div className="activity-content">
                        <div className="activity-description">
                          <span className="activity-user">{activity.user_name}</span>
                          <span className="activity-text"> {activity.description}</span>
                        </div>
                        <div className="activity-meta">
                          <span className="activity-role">{activity.user_role.replace(/_/g, ' ')}</span>
                          {activity.branch && <span className="activity-branch">{activity.branch.name}</span>}
                          <span className="activity-time">{formatActivityTime(activity.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">No recent activities to display.</div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
